export const VISUAL_CONFIG_STORAGE_KEY = "hexplode.visual-config.v1";

export const DEFAULT_VISUAL_CONFIG = Object.freeze({
  neutralTile: "#4f4f4f",
  redTile: "#b15967",
  blueTile: "#4288ae",
  redTileGlow: "#ffffff",
  blueTileGlow: "#ffffff",
  neutralBrightness: 0.45,
  tileBrightness: 0.3,
  tileGlow: 0,
  hoverGlow: 0.45,
  tileOutline: 1,
  redOrb: "#db0030",
  blueOrb: "#0087db",
  redHot: "#ff7a8e",
  blueHot: "#4d839d",
  orbBaseGlow: 0,
  orbGlowPerBall: 0.07,
  primedGlowBoost: 1.02,
  whiteningPower: 1.15,
  orbHighlightMix: 0.22,
  orbBaseWhitening: 0.2,
  orbGlowWhiteMix: 0.37,
  bloomStrength: 0.39,
  bloomRadius: 0.32,
  bloomThreshold: 0.47,
  exposure: 1.3,
});

export const VISUAL_CONTROL_GROUPS = Object.freeze([
  {
    id: "tiles",
    label: "Tiles",
    controls: [
      { key: "neutralTile", label: "Neutral tile", type: "color" },
      { key: "redTile", label: "Red tile", type: "color" },
      { key: "blueTile", label: "Blue tile", type: "color" },
      { key: "redTileGlow", label: "Red tile glow", type: "color" },
      { key: "blueTileGlow", label: "Blue tile glow", type: "color" },
      { key: "neutralBrightness", label: "Neutral brightness", type: "range", min: 0.3, max: 1.2, step: 0.01 },
      { key: "tileBrightness", label: "Owned brightness", type: "range", min: 0.3, max: 1.3, step: 0.01 },
      { key: "tileGlow", label: "Owned glow", type: "range", min: 0, max: 1.5, step: 0.01 },
      { key: "hoverGlow", label: "Hover glow", type: "range", min: 0, max: 2, step: 0.01 },
      { key: "tileOutline", label: "Outline", type: "range", min: 0, max: 1, step: 0.01 },
    ],
  },
  {
    id: "balls",
    label: "Balls",
    controls: [
      { key: "redOrb", label: "Red ball", type: "color" },
      { key: "blueOrb", label: "Blue ball", type: "color" },
      { key: "redHot", label: "Red hot core", type: "color" },
      { key: "blueHot", label: "Blue hot core", type: "color" },
      { key: "orbBaseGlow", label: "Base glow", type: "range", min: 0, max: 3, step: 0.01 },
      { key: "orbGlowPerBall", label: "Glow per ball", type: "range", min: 0, max: 0.8, step: 0.01 },
      { key: "primedGlowBoost", label: "Primed boost", type: "range", min: 0, max: 4, step: 0.01 },
      { key: "whiteningPower", label: "Whitening curve", type: "range", min: 0.3, max: 5, step: 0.05 },
      { key: "orbHighlightMix", label: "Hot core mix", type: "range", min: 0, max: 1, step: 0.01 },
      { key: "orbBaseWhitening", label: "Base whitening", type: "range", min: 0, max: 0.5, step: 0.01 },
      { key: "orbGlowWhiteMix", label: "Glow whitening", type: "range", min: 0, max: 1, step: 0.01 },
    ],
  },
  {
    id: "scene",
    label: "Scene",
    controls: [
      { key: "bloomStrength", label: "Bloom strength", type: "range", min: 0, max: 2.5, step: 0.01 },
      { key: "bloomRadius", label: "Bloom radius", type: "range", min: 0, max: 1, step: 0.01 },
      { key: "bloomThreshold", label: "Bloom threshold", type: "range", min: 0, max: 1.5, step: 0.01 },
      { key: "exposure", label: "Exposure", type: "range", min: 0.5, max: 2.5, step: 0.01 },
    ],
  },
]);

const CONTROL_BY_KEY = new Map(
  VISUAL_CONTROL_GROUPS.flatMap((group) => group.controls.map((control) => [control.key, control])),
);

export function normalizeVisualConfig(input = {}) {
  const normalized = { ...DEFAULT_VISUAL_CONFIG };
  for (const [key, fallback] of Object.entries(DEFAULT_VISUAL_CONFIG)) {
    const value = input[key];
    if (typeof fallback === "string") {
      if (typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value)) normalized[key] = value.toLowerCase();
      continue;
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) continue;
    const control = CONTROL_BY_KEY.get(key);
    normalized[key] = control
      ? Math.min(control.max, Math.max(control.min, numeric))
      : numeric;
  }
  return normalized;
}
